import React, { useEffect, useState, useCallback } from 'react';
import { CheckCircle2, Crown, Mail, LogIn, MapPin, Loader2, Info } from 'lucide-react';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';
import Header from '../Header';
import { Button } from '../ui/button';
import { useAuth } from '../../context/AuthContext';
import LoginModal from '../LoginModal';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const PremiumThankYouPage = () => {
  const navigate = useNavigate();
  const { isAuthenticated } = useAuth();
  const [premium, setPremium] = useState(false);
  const [loading, setLoading] = useState(true);
  const [loginOpen, setLoginOpen] = useState(false);

  const checkEntitlement = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await axios.get(`${API}/bimmap/entitlement`);
      setPremium(!!data?.premium);
    } catch {
      setPremium(false);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { checkEntitlement(); }, [checkEntitlement, isAuthenticated]);

  return (
    <div className="min-h-screen bg-black" data-testid="bimmap-thankyou-page">
      <Header />
      <section className="relative pt-32 pb-20 overflow-hidden">
        <div className="absolute inset-0 opacity-10 pointer-events-none">
          <div className="absolute top-0 left-1/2 -translate-x-1/2 w-[32rem] h-[32rem] bg-red-600 rounded-full blur-3xl" />
        </div>
        <div className="container mx-auto px-4 lg:px-8 relative z-10 max-w-2xl text-center">
          <div className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-green-500/15 border border-green-500/40 mb-6">
            <CheckCircle2 className="text-green-400" size={34} />
          </div>
          <h1 className="text-3xl sm:text-4xl font-bold text-white mb-3">Compra confirmada! 🎉</h1>
          <p className="text-gray-300 text-base md:text-lg mb-8">
            Obrigado por assinar o <strong className="text-white">AJ | BIM Map Premium</strong>. Seus contatos e benefícios estão a um passo.
          </p>

          {loading ? (
            <div className="flex justify-center py-6"><Loader2 className="animate-spin text-red-500" size={28} /></div>
          ) : premium ? (
            <div className="bg-green-500/10 border border-green-500/30 rounded-2xl p-6 text-left mb-8" data-testid="bimmap-thankyou-active">
              <div className="flex items-center gap-2 text-green-300 font-semibold mb-2"><Crown size={18} /> Premium ativo</div>
              <p className="text-sm text-gray-300">Sua conta já está com o acesso liberado. Todos os contatos dos perfis do mapa estão desbloqueados.</p>
              <Button onClick={() => navigate('/bimmap')} className="mt-4 bg-red-600 hover:bg-red-700 text-white" data-testid="bimmap-thankyou-go-map">
                <MapPin size={16} className="mr-2" /> Explorar o BIMMap
              </Button>
            </div>
          ) : (
            <div className="bg-amber-500/10 border border-amber-500/30 rounded-2xl p-6 text-left mb-8" data-testid="bimmap-thankyou-instructions">
              <div className="flex items-center gap-2 text-amber-300 font-semibold mb-3"><Mail size={18} /> Como liberar seu acesso</div>
              <ul className="space-y-2.5 text-sm text-gray-300">
                <li className="flex items-start gap-2"><Info size={15} className="text-amber-300 mt-0.5 shrink-0" /> Use a conta com o <strong className="text-white">mesmo e-mail</strong> que você informou na compra da Hotmart.</li>
                <li className="flex items-start gap-2"><Info size={15} className="text-amber-300 mt-0.5 shrink-0" /> Já tem conta? Faça login que o acesso é liberado automaticamente.</li>
                <li className="flex items-start gap-2"><Info size={15} className="text-amber-300 mt-0.5 shrink-0" /> Ainda não tem conta? Cadastre-se com esse e-mail e o Premium é ativado no primeiro acesso.</li>
              </ul>
              <div className="flex flex-wrap gap-3 mt-5">
                {!isAuthenticated && (
                  <Button onClick={() => setLoginOpen(true)} className="bg-red-600 hover:bg-red-700 text-white" data-testid="bimmap-thankyou-login">
                    <LogIn size={16} className="mr-2" /> Fazer login / criar conta
                  </Button>
                )}
                <Button variant="outline" onClick={checkEntitlement} className="border-zinc-600 text-white hover:bg-zinc-800" data-testid="bimmap-thankyou-recheck">
                  Já entrei — verificar acesso
                </Button>
                <Button variant="ghost" onClick={() => navigate('/bimmap')} className="text-gray-300 hover:text-white" data-testid="bimmap-thankyou-map">
                  <MapPin size={16} className="mr-2" /> Ir para o BIMMap
                </Button>
              </div>
            </div>
          )}

          <p className="text-xs text-gray-500">
            A liberação pode levar alguns instantes após a confirmação do pagamento pela Hotmart. Se demorar, faça login novamente ou use o botão de verificar acesso.
          </p>
        </div>
      </section>
      <LoginModal isOpen={loginOpen} onClose={() => { setLoginOpen(false); checkEntitlement(); }} />
    </div>
  );
};

export default PremiumThankYouPage;
